import { Checkbox } from "@/components/ui/checkbox";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/api";

const GUIDA = { esclusiva: "🔒 Esclusiva", esperta: "🎓 Esperta", libera: "🚗 Libera" };
const cap = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : "—");

function perRata(v) {
  const n = parseInt(v.installments_count, 10) || 1;
  return (Number(v.amount) || 0) / n;
}

function covKey(c) {
  return (c.name || "").trim().toLowerCase();
}

export function VariantsCompareTable({ variants, cheapestId, winnerId, selectedIds, onToggle, fallbackDrivingType }) {
  const covNames = [];
  const seen = new Set();
  variants.forEach((v) => (v.coverages || []).forEach((c) => {
    const k = covKey(c);
    if (k && !seen.has(k)) { seen.add(k); covNames.push({ k, name: c.name, acc: c.kind === "accessory" }); }
  }));
  covNames.sort((a, b) => Number(a.acc) - Number(b.acc));
  const minTotal = Math.min(...variants.map((v) => Number(v.amount) || Infinity));

  const rows = [
    ["Broker", (v) => v.broker_name || "—"],
    ["Tipo guida", (v) => GUIDA[(v.driving_type || fallbackDrivingType || "").toLowerCase()] || "—"],
    ["Premio per rata", (v) => <span className="font-semibold text-blue-700">{formatCurrency(perRata(v))}</span>],
    ["Frazionamento", (v) => cap(v.frazionamento)],
    ["N° rate", (v) => v.installments_count || 1],
    ["Totale", (v) => (
      <span className={`font-bold tabular-nums ${Number(v.amount) === minTotal ? "text-emerald-700" : "text-slate-900"}`}>{formatCurrency(v.amount)}</span>
    )],
    ["Decorrenza", (v) => (v.effective_date ? formatDate(v.effective_date) : "—")],
    ["Scadenza", (v) => (v.expiry_date ? formatDate(v.expiry_date) : "—")],
  ];

  const headCls = (v) => (v.id === winnerId ? "bg-emerald-100" : v.id === cheapestId ? "bg-amber-100" : v.is_featured ? "bg-violet-100" : "bg-slate-100");

  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 mb-3" data-testid="variants-compare-table">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-white text-left p-2 border-b border-slate-200 min-w-[140px]" />
            {variants.map((v) => (
              <th key={v.id} className={`p-2 border-b border-l border-slate-200 text-left align-top min-w-[150px] ${headCls(v)}`} data-testid={`compare-col-${v.id}`}>
                <div className="flex items-start gap-1.5">
                  <Checkbox checked={selectedIds.has(v.id)} onCheckedChange={() => onToggle(v.id)} data-testid={`compare-select-${v.id}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-bold text-slate-900 truncate">{v.company}</div>
                    <div className="flex flex-wrap gap-1 mt-0.5">
                      {v.id === winnerId && <span className="text-[9px] font-bold uppercase bg-emerald-600 text-white px-1 rounded">🏆 Finalizzata</span>}
                      {v.id === cheapestId && v.id !== winnerId && <span className="text-[9px] font-bold uppercase bg-amber-500 text-white px-1 rounded">🥇 Migliore</span>}
                      {v.is_featured && <span className="text-[9px] font-bold uppercase bg-violet-600 text-white px-1 rounded">⭐ Evidenza</span>}
                    </div>
                    {v.created_at && <div className="text-[9.5px] font-normal text-slate-500 mt-0.5">🕒 {formatDateTime(v.created_at)}</div>}
                  </div>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, fn]) => (
            <tr key={label} className="odd:bg-white even:bg-slate-50/60">
              <td className="sticky left-0 z-10 bg-inherit p-2 font-semibold text-slate-600 border-b border-slate-100">{label}</td>
              {variants.map((v) => <td key={v.id} className="p-2 border-b border-l border-slate-100 tabular-nums">{fn(v)}</td>)}
            </tr>
          ))}
          {covNames.length > 0 && (
            <tr><td colSpan={variants.length + 1} className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wide font-semibold text-slate-500 bg-white">Garanzie (premio per rata)</td></tr>
          )}
          {covNames.map((cn) => (
            <tr key={cn.k} className="odd:bg-white even:bg-slate-50/60" data-testid={`compare-cov-row-${cn.k}`}>
              <td className={`sticky left-0 z-10 bg-inherit p-2 border-b border-slate-100 ${cn.acc ? "text-slate-500 italic" : "text-slate-700"}`}>{cn.name}</td>
              {variants.map((v) => {
                const c = (v.coverages || []).find((x) => covKey(x) === cn.k);
                return (
                  <td key={v.id} className="p-2 border-b border-l border-slate-100 tabular-nums">
                    {!c ? <span className="text-slate-300">—</span> : (
                      <span className={c.client_visible === false ? "opacity-50 line-through" : ""}>
                        <span className="text-emerald-600 mr-1">✓</span>
                        {c.premium != null && c.premium !== "" ? formatCurrency(c.premium) : "incl."}
                        {c.sum_insured ? <span className="block text-[10px] text-slate-500">mass. {formatCurrency(c.sum_insured)}</span> : null}
                        {c.deductible ? <span className="block text-[10px] text-slate-500">fr. {c.deductible}</span> : null}
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
          {variants.some((v) => (v.notes || "").trim()) && (
            <tr className="bg-amber-50/50">
              <td className="sticky left-0 z-10 bg-inherit p-2 font-semibold text-slate-600">Note</td>
              {variants.map((v) => <td key={v.id} className="p-2 border-l border-slate-100 italic text-slate-700 whitespace-pre-wrap">{v.notes || "—"}</td>)}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default VariantsCompareTable;
